import { sameCityCheckEnabled } from "@/consts"
import type { DeliveryAreaConfig, DeliveryAreaNeighborhood } from "@/lib/delivery-area/types"
import { Address, Polygon, ServiceArea } from "@/types"
import * as turf from "@turf/turf"

type GeoCoordinate = {
  latitude: number
  longitude: number
}

function hasGeoCoordinates(geoCoordinates: GeoCoordinate[] | undefined): geoCoordinates is GeoCoordinate[] {
  return Array.isArray(geoCoordinates) && geoCoordinates.length >= 3
}

/** Narrows any polygon-shaped record (delivery or exclusion) to one with a real drawn ring. */
function isGeoPolygon<T extends { geoCoordinates?: GeoCoordinate[] }>(
  polygon: T,
): polygon is T & { geoCoordinates: GeoCoordinate[] } {
  return hasGeoCoordinates(polygon.geoCoordinates)
}

function isPointInPolygon(point: ReturnType<typeof turf.point>, geoCoordinates: GeoCoordinate[]): boolean {
  const coords = geoCoordinates.map((coordinate) => [coordinate.longitude, coordinate.latitude])
  coords.push(coords[0])
  const turfPolygon = turf.polygon([coords])
  return turf.booleanPointInPolygon(point, turfPolygon)
}

function normalize(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
}

function hasUsableCoordinates(address: Address): boolean {
  return (
    Number.isFinite(address.latitude) &&
    Number.isFinite(address.longitude) &&
    !(address.latitude === 0 && address.longitude === 0)
  )
}

/**
 * True when the address's UF matches the merchant's, and — only when
 * `sameCityCheckEnabled` — its city matches too. Shared by every rule that
 * has no served-city list of its own to check against (the flat NEIGHBORHOOD
 * mode, and any address gate that predates a city).
 */
function passesMerchantStateAndCityGate(address: Address, merchantAddress: Address | undefined): boolean {
  if (!merchantAddress?.state || !address.state) return false
  if (normalize(address.state) !== normalize(merchantAddress.state)) return false

  if (!sameCityCheckEnabled) return true

  const merchantCity = merchantAddress.city?.trim() || merchantAddress.district?.trim()
  if (!merchantCity || !address.city) return false
  return normalize(address.city) === normalize(merchantCity)
}

type NeighborhoodByName = Map<string, DeliveryAreaNeighborhood>

type DeliveryAreaIndex = {
  citiesByKey: Map<string, NeighborhoodByName>
  flatNeighborhoods: NeighborhoodByName
}

function cityKey(state: string, city: string): string {
  return `${normalize(state)}|${normalize(city)}`
}

function indexNeighborhoods(neighborhoods: DeliveryAreaNeighborhood[]): NeighborhoodByName {
  const byName: NeighborhoodByName = new Map()
  for (const neighborhood of neighborhoods) byName.set(normalize(neighborhood.name), neighborhood)
  return byName
}

/** One index per config object, built once and reused across every address it evaluates. */
const indexCache = new WeakMap<DeliveryAreaConfig, DeliveryAreaIndex>()

function buildDeliveryAreaIndex(
  config: Extract<DeliveryAreaConfig, { mode: "NEIGHBORHOOD" }>,
): DeliveryAreaIndex {
  const cached = indexCache.get(config)
  if (cached) return cached

  const citiesByKey = new Map<string, NeighborhoodByName>()
  for (const city of config.cities) {
    citiesByKey.set(cityKey(city.state, city.name), indexNeighborhoods(city.neighborhoods))
  }

  const index: DeliveryAreaIndex = {
    citiesByKey,
    flatNeighborhoods: indexNeighborhoods(config.neighborhoods),
  }
  indexCache.set(config, index)
  return index
}

/** A served city matched by UF + name; its neighborhood list is the only one that can match the district. */
function matchesCityNeighborhood(address: Address, index: DeliveryAreaIndex): DeliveryAreaNeighborhood | undefined {
  if (!address.state || !address.city || !address.district) return undefined
  const neighborhoods = index.citiesByKey.get(cityKey(address.state, address.city))
  return neighborhoods?.get(normalize(address.district))
}

/** City-less NEIGHBORHOOD mode: today's UF(+city) gate, then a name lookup in the flat list. */
function matchesFlatNeighborhood(
  address: Address,
  merchantAddress: Address | undefined,
  index: DeliveryAreaIndex,
): DeliveryAreaNeighborhood | undefined {
  if (!address.district) return undefined
  if (!passesMerchantStateAndCityGate(address, merchantAddress)) return undefined
  return index.flatNeighborhoods.get(normalize(address.district))
}

function neighborhoodAsPolygon(neighborhood: DeliveryAreaNeighborhood): Polygon {
  return {
    name: neighborhood.name,
    price: { value: neighborhood.deliveryFeeReais, currency: "BRL" },
    estimateDeliveryTime: neighborhood.deliveryTimeMinutes ?? 0,
  }
}

/**
 * True when the address has a drawn polygon ring — the only shape POLYGON
 * mode ever matches against.
 */
function hasDrawnPolygon(serviceArea: ServiceArea | null | undefined): boolean {
  return (serviceArea?.polygon ?? []).some(isGeoPolygon)
}

/**
 * True when the area can, in principle, deliver somewhere: a confirmed
 * NEIGHBORHOOD config (its coverage may still be empty — every address then
 * simply fails to match), or a catalog service area with a real drawn
 * polygon ring.
 *
 * A `null` config (still loading, or the endpoint failed) behaves like
 * POLYGON: safe because that branch never depended on the new endpoint, and
 * a NEIGHBORHOOD-only catalog area (named entries, no geo ring) correctly
 * comes back unusable instead of being guessed at.
 */
export function isUsableServiceArea(
  serviceArea: ServiceArea | null | undefined,
  deliveryAreaConfig?: DeliveryAreaConfig | null,
): boolean {
  if (deliveryAreaConfig?.mode === "NEIGHBORHOOD") return true
  return hasDrawnPolygon(serviceArea)
}

/**
 * Single entry point every checkout/address surface resolves an address
 * against. NEIGHBORHOOD (per `deliveryAreaConfig`) matches by served
 * city+neighborhood or, city-less, by UF(+city)+neighborhood name; POLYGON
 * (config says so, or unknown) runs the unchanged point-in-polygon check
 * against the catalog's drawn rings. A matched neighborhood is returned as a
 * synthetic single-entry `Polygon[]` so every downstream reader (price,
 * ETA, `pickDeliveryPolygon`) keeps working unmodified.
 */
export function getAddressServiceAreaStatus(
  address: Address,
  serviceArea: ServiceArea | null | undefined,
  merchantAddress?: Address,
  deliveryAreaConfig?: DeliveryAreaConfig | null,
): { availablePolygons: Polygon[]; minPriceAvailable: number } {
  if (deliveryAreaConfig?.mode === "NEIGHBORHOOD") {
    const index = buildDeliveryAreaIndex(deliveryAreaConfig)
    const neighborhood =
      deliveryAreaConfig.cities.length > 0
        ? matchesCityNeighborhood(address, index)
        : matchesFlatNeighborhood(address, merchantAddress, index)

    if (!neighborhood) return { availablePolygons: [], minPriceAvailable: 0 }
    const polygon = neighborhoodAsPolygon(neighborhood)
    return { availablePolygons: [polygon], minPriceAvailable: polygon.price.value }
  }

  const geoUsable = hasUsableCoordinates(address)
  const point = geoUsable ? turf.point([address.longitude, address.latitude]) : null

  const polygons = (serviceArea?.polygon ?? []).filter(isGeoPolygon)
  const availablePolygons = polygons.filter(
    (polygon) => !point || isPointInPolygon(point, polygon.geoCoordinates),
  )

  const exclusionPolygons = (serviceArea?.exclusionAreaPolygon ?? []).filter(isGeoPolygon)
  const isExcluded = point
    ? exclusionPolygons.some((exclusion) => isPointInPolygon(point, exclusion.geoCoordinates))
    : false

  if (isExcluded || availablePolygons.length === 0) {
    return { availablePolygons: [], minPriceAvailable: 0 }
  }

  const minPriceAvailable = availablePolygons.reduce((min, current) => {
    return Math.min(min, current.price.value)
  }, availablePolygons[0].price.value)

  return { availablePolygons, minPriceAvailable }
}

export function pickDeliveryPolygon(
  availablePolygons: Polygon[],
  minPriceAvailable: number,
): Polygon | undefined {
  if (availablePolygons.length === 0) return undefined
  return availablePolygons.find((p) => p.price.value === minPriceAvailable) ?? availablePolygons[0]
}

export function selectDeliveryPolygon(
  address: Address,
  serviceArea: ServiceArea | null | undefined,
  merchantAddress?: Address,
  deliveryAreaConfig?: DeliveryAreaConfig | null,
): Polygon | undefined {
  const { availablePolygons, minPriceAvailable } = getAddressServiceAreaStatus(
    address,
    serviceArea,
    merchantAddress,
    deliveryAreaConfig,
  )
  return pickDeliveryPolygon(availablePolygons, minPriceAvailable)
}

/**
 * The cheapest fee the store advertises before an address is known ("a
 * partir de R$ X"): the minimum across every served neighborhood in
 * NEIGHBORHOOD mode, or across the catalog's polygons otherwise. `0` reads as
 * "grátis" at the call site, matching the pre-existing polygon behaviour.
 */
export function minDeliveryFee(
  serviceArea: ServiceArea | null | undefined,
  deliveryAreaConfig?: DeliveryAreaConfig | null,
): number {
  if (deliveryAreaConfig?.mode === "NEIGHBORHOOD") {
    const neighborhoods =
      deliveryAreaConfig.cities.length > 0
        ? deliveryAreaConfig.cities.flatMap((city) => city.neighborhoods)
        : deliveryAreaConfig.neighborhoods
    if (neighborhoods.length === 0) return 0
    return neighborhoods.reduce((min, n) => Math.min(min, n.deliveryFeeReais), neighborhoods[0].deliveryFeeReais)
  }

  const polygons = serviceArea?.polygon
  if (!polygons || polygons.length === 0) return 0
  return polygons.reduce((min, polygon) => Math.min(min, polygon.price.value), polygons[0].price.value)
}
